import React, {createContext, useCallback, useContext, useRef, useState} from 'react';
import styled from 'styled-components';
import {CheckCircle2, FileText, LockKeyhole, Upload} from 'lucide-react';
import {apiErrorMessage} from '../api/abfApi';
import type {ActionFeedbackState} from '../components/ActionFeedback';
import {Button, INK} from '../onboarding/review/reviewStyles';
import {formatAmountWithCommas, parseAmountShorthand} from '../../../utils/amountUtils';
import {useAuth} from '../../../contexts/AuthContext';
import {ffApi} from './forwardFlowApi';

// --- Feedback --------------------------------------------------------------------------------

/** One place a forward-flow action reports to: the program page's toast. */
const Feedback = createContext<(feedback: ActionFeedbackState | null) => void>(() => undefined);
export const FeedbackProvider = Feedback.Provider;
export const useFeedback = () => useContext(Feedback);

/**
 * Run a command, say what happened, refresh. The refusal text is the server's own words,
 * which name the record and what would work.
 */
export function useCommand(refresh?: () => Promise<unknown> | void) {
  const notify = useFeedback();
  const [busy, setBusy] = useState(false);
  const run = useCallback(async <T,>(work: () => Promise<T>, done: string | ((result: T) => string),
      fallback = 'The action could not be completed.'): Promise<T | null> => {
    setBusy(true);
    try {
      const result = await work();
      notify({tone: 'success', message: typeof done === 'function' ? done(result) : done});
      await refresh?.();
      return result;
    } catch (e) {
      notify({tone: 'error', message: apiErrorMessage(e, fallback)});
      return null;
    } finally {
      setBusy(false);
    }
  }, [notify, refresh]);
  return {busy, run};
}

// --- Roles -----------------------------------------------------------------------------------

const MAKERS = new Set(['PM', 'PORTFOLIO_MANAGER', 'TRADER', 'OPERATIONS', 'OPERATIONS_ANALYST',
  'MIDDLE_OFFICE_ADMIN', 'ADMIN', 'PLATFORM_ADMIN']);
const CHECKERS = new Set(['RISK', 'RISK_MANAGER', 'COMPLIANCE', 'COMPLIANCE_OFFICER',
  'MIDDLE_OFFICE_ADMIN', 'ADMIN', 'PLATFORM_ADMIN']);
export function useViewer() {
  const {user} = useAuth();
  const role = (user?.role ?? '').replace(/^ROLE_/, '').toUpperCase();
  return {username: user?.username as string | undefined, role, maker: MAKERS.has(role), checker: CHECKERS.has(role)};
}

// --- Amounts ---------------------------------------------------------------------------------

/** A money entry as a number: 5MM, 250K and 1,250.50 all parse; a leading minus is kept. */
export const amountOf = (text: string | number | null | undefined): number | null => {
  if (text === null || text === undefined) return null;
  const raw = String(text).trim();
  if (raw === '') return null;
  const negative = /^[-−(]/.test(raw);
  const body = raw.replace(/^[-−(]\s*/, '').replace(/\)$/, '');
  if (!/^[$€£¥]?\s*[\d.,\s]+(?:MMM|MM|M|K|B)?$/i.test(body)) return null;
  const value = parseAmountShorthand(body);
  if (!Number.isFinite(value)) return null;
  if (value === 0 && !/^[$€£¥]?\s*0*(?:\.0*)?$/.test(body)) return null;
  return negative ? -value : value;
};

const AmountBox = styled.input<{$bad: boolean}>`
  font-variant-numeric: tabular-nums;
  ${p => p.$bad ? 'border-color: #f87171 !important;' : ''}
`;

/**
 * Every money field in forward flow: type 5MM or 250K and it becomes the full number on
 * leaving the field. The value stays text so a typo remains visible instead of vanishing.
 */
export function AmountInput({value, onChange, signed = false, placeholder, disabled, id, 'aria-label': ariaLabel}: {
  value: string; onChange: (text: string) => void; signed?: boolean; placeholder?: string; disabled?: boolean;
  id?: string; 'aria-label'?: string;
}) {
  const parsed = amountOf(value);
  const bad = value.trim() !== '' && (parsed === null || (!signed && parsed < 0));
  return <AmountBox id={id} aria-label={ariaLabel} $bad={bad} aria-invalid={bad} inputMode="decimal" disabled={disabled}
    value={value} placeholder={placeholder ?? 'e.g. 250K, 5MM or 1,250.50'}
    title={bad ? 'Enter an amount such as 1,250.50, 250K or 5MM' : undefined}
    onChange={e => onChange(e.target.value)}
    onBlur={e => {
      const n = amountOf(e.target.value);
      if (n !== null) onChange(`${n < 0 ? '-' : ''}${formatAmountWithCommas(Math.abs(n))}`);
    }} />;
}

// --- Evidence --------------------------------------------------------------------------------

export type StoredEvidence = {storageUri: string; sourceSha256: string; fileName: string};

const EvidenceBox = styled.div`
  min-width: 0; padding: 12px 14px; border: 1px dashed ${INK.lineStrong}; border-radius: 9px; background: ${INK.raised};
  &[data-stored='true'] { border-style: solid; border-color: #376e60; }
  .label { display: flex; align-items: center; gap: 7px; color: ${INK.body}; font-size: 13px; font-weight: 650; }
  .hint { margin: 4px 0 9px; color: ${INK.muted}; font-size: 12px; line-height: 1.45; }
  .row { display: flex; flex-wrap: wrap; align-items: center; gap: 9px; }
  .stored { display: inline-flex; align-items: center; gap: 6px; color: #7cddbe; font-size: 13px; overflow-wrap: anywhere; }
  .problem { margin-top: 7px; color: #fca5a5; font-size: 12px; }
  details { margin-top: 7px; color: ${INK.faint}; font-size: 11px; overflow-wrap: anywhere; }
  summary { cursor: pointer; }
`;

/**
 * Evidence for one action. Choosing the file stores it; there is no second button. The
 * stored file is registered to the program with its fingerprint, which the action sends.
 */
export function EvidenceField({programId, label, hint, value, onChange, accept = '.pdf,.csv', disabled}: {
  programId: number; label: string; hint?: string; value: StoredEvidence | null;
  onChange: (evidence: StoredEvidence | null) => void; accept?: string; disabled?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [storing, setStoring] = useState(false);
  const [problem, setProblem] = useState('');
  const choose = async (file: File | null) => {
    if (!file) return;
    setStoring(true); setProblem('');
    try { onChange(await ffApi.uploadDecisionEvidence(programId, file)); }
    catch (e) { onChange(null); setProblem(apiErrorMessage(e, `${file.name} could not be stored.`)); }
    finally { setStoring(false); if (input.current) input.current.value = ''; }
  };
  return <EvidenceBox data-stored={!!value}>
    <div className="label"><FileText size={15} aria-hidden="true" />{label}</div>
    {hint && <div className="hint">{hint}</div>}
    <input ref={input} type="file" hidden accept={accept} aria-label={label} onChange={e => void choose(e.target.files?.[0] ?? null)} />
    <div className="row">
      {value && <span className="stored"><CheckCircle2 size={15} aria-hidden="true" />{value.fileName} stored</span>}
      <Button type="button" disabled={disabled || storing} onClick={() => input.current?.click()}>
        <Upload size={14} aria-hidden="true" />{storing ? 'Storing…' : value ? 'Replace' : 'Choose file'}</Button>
    </div>
    {problem && <div className="problem" role="alert">{problem}</div>}
    {value && <details><summary>File fingerprint</summary>SHA-256 {value.sourceSha256}</details>}
  </EvidenceBox>;
}

// --- Confirmation ----------------------------------------------------------------------------

const ConfirmPanel = styled.div`
  margin-top: 10px; padding: 14px 16px; border: 1px solid #43617f; border-radius: 10px; background: #142438;
  strong { display: block; color: ${INK.strong}; font-size: 14px; }
  p { margin: 5px 0 12px; color: ${INK.soft}; font-size: 13px; line-height: 1.5; white-space: pre-line; }
  .row { display: flex; flex-wrap: wrap; gap: 9px; }
`;
const Why = styled.div`
  display: flex; align-items: flex-start; gap: 6px; margin-top: 7px; color: #edcc82; font-size: 12px; line-height: 1.45;
`;

/**
 * A consequential action asks once, in the page, in words: what will happen and what will
 * not. When the action is unavailable the reason sits under the button, never in a tooltip.
 */
export function ConfirmAction({label, title, body, confirmLabel, disabled, disabledReason, busy, variant = 'primary', onConfirm}: {
  label: React.ReactNode; title: string; body: string; confirmLabel?: string; disabled?: boolean; disabledReason?: string | null;
  busy?: boolean; variant?: 'primary' | 'ghost' | 'danger'; onConfirm: () => void | Promise<unknown>;
}) {
  const [asking, setAsking] = useState(false);
  const blocked = disabled || !!disabledReason;
  return <div>
    {!asking && <Button type="button" $variant={variant} disabled={blocked || busy} onClick={() => setAsking(true)}>
      {blocked && <LockKeyhole size={14} aria-hidden="true" />}{busy ? 'Working…' : label}</Button>}
    {!asking && disabledReason && <Why><LockKeyhole size={13} aria-hidden="true" /><span>{disabledReason}</span></Why>}
    {asking && <ConfirmPanel role="alertdialog" aria-label={title}>
      <strong>{title}</strong><p>{body}</p>
      <div className="row">
        <Button type="button" $variant={variant} disabled={busy} autoFocus
          onClick={() => { setAsking(false); void onConfirm(); }}>{confirmLabel ?? label}</Button>
        <Button type="button" onClick={() => setAsking(false)}>Go back</Button>
      </div>
    </ConfirmPanel>}
  </div>;
}

/** A plain action with its reason shown when it cannot be taken. */
export function GatedAction({children, disabled, reason, busy, variant, onClick}: {
  children: React.ReactNode; disabled?: boolean; reason?: string | null; busy?: boolean;
  variant?: 'primary' | 'ghost' | 'danger'; onClick: () => void;
}) {
  const blocked = disabled || !!reason;
  return <div>
    <Button type="button" $variant={variant} disabled={blocked || busy} onClick={onClick}>
      {reason && <LockKeyhole size={14} aria-hidden="true" />}{busy ? 'Working…' : children}</Button>
    {reason && <Why><LockKeyhole size={13} aria-hidden="true" /><span>{reason}</span></Why>}
  </div>;
}

// --- Surfaces on the shared tokens ------------------------------------------------------------

export const Section = styled.section`
  min-width: 0; padding: 18px 20px; margin: 0 0 16px; border: 1px solid ${INK.line}; border-radius: 12px; background: ${INK.panel};
  > header { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: flex-start; gap: 14px; margin-bottom: 14px; }
  > header h2 { margin: 0; color: ${INK.strong}; font-size: 16px; }
  > header p { margin: 4px 0 0; color: ${INK.muted}; font-size: 13px; line-height: 1.5; max-width: 78ch; }
  h3 { margin: 18px 0 8px; color: ${INK.strong}; font-size: 14px; }
  p { color: ${INK.muted}; font-size: 13px; line-height: 1.55; }
  label { display: grid; gap: 5px; color: ${INK.soft}; font-size: 12px; font-weight: 600; min-width: 0; }
  label small { color: ${INK.faint}; font-weight: 400; line-height: 1.4; }
  input:not([type='checkbox']):not([type='file']), select, textarea { box-sizing: border-box; width: 100%; min-height: 38px; padding: 7px 10px;
    color: ${INK.body}; background: ${INK.well}; border: 1px solid ${INK.lineStrong}; border-radius: 7px; font: inherit; font-size: 13px; }
  input:focus-visible, select:focus-visible, textarea:focus-visible { outline: 2px solid ${INK.accent}; outline-offset: 1px; }
  input::placeholder, textarea::placeholder { color: ${INK.faint}; }
  select { appearance: none; -webkit-appearance: none; -moz-appearance: none; cursor: pointer; padding-right: 32px;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%238b9bb0' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>");
    background-repeat: no-repeat; background-position: right 11px center; }
  textarea { min-height: 72px; resize: vertical; }
  .muted { color: ${INK.muted}; }
  .num { font-variant-numeric: tabular-nums; text-align: right; white-space: nowrap; }
`;

export const Fields = styled.div<{$cols?: number}>`
  display: grid; grid-template-columns: repeat(${p => p.$cols ?? 3}, minmax(0, 1fr)); gap: 14px; margin: 10px 0 14px; align-items: start;
  @media (max-width: 1000px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

export const Grid = styled.table`
  border-collapse: collapse; width: 100%; font-size: 13px;
  th, td { padding: 9px 8px; border-bottom: 1px solid ${INK.line}; text-align: left; vertical-align: top; }
  th { color: ${INK.faint}; font-size: 11px; letter-spacing: .04em; text-transform: uppercase; font-weight: 700; white-space: nowrap; }
  td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  tfoot td { font-weight: 700; border-top: 1px solid ${INK.lineStrong}; border-bottom: 0; }
  tr[data-attention='true'] td:first-child { box-shadow: inset 3px 0 #f6c453; }
  tr[data-selected='true'] td { background: #19334a; }
  tbody tr:hover td { background: #11233a; }
  button.link { border: 0; padding: 0; background: transparent; color: ${INK.accentSoft}; cursor: pointer; font: inherit; text-align: left; }
  button.link:hover { text-decoration: underline; }
`;
export const Scroller = styled.div`overflow-x: auto;`;
export const Row = styled.div`display: flex; flex-wrap: wrap; gap: 10px; align-items: flex-start;`;

export const FactStrip = styled.div<{$cols?: number}>`
  display: grid; grid-template-columns: repeat(${p => p.$cols ?? 4}, minmax(0, 1fr)); gap: 1px; margin: 0 0 16px;
  border: 1px solid ${INK.line}; border-radius: 10px; overflow: hidden; background: ${INK.line};
  > div { background: ${INK.raised}; padding: 12px 15px; min-width: 0; }
  span { display: block; color: ${INK.faint}; font-size: 11px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; }
  strong { display: block; margin-top: 4px; color: ${INK.strong}; font-size: 17px; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
  small { display: block; margin-top: 3px; color: ${INK.muted}; font-size: 12px; line-height: 1.4; }
  @media (max-width: 1000px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
`;

export const Notice = styled.div<{$tone?: 'info' | 'warn' | 'error' | 'ok'}>`
  padding: 12px 14px; border-radius: 9px; margin: 0 0 14px; font-size: 13px; line-height: 1.55;
  border: 1px solid ${p => p.$tone === 'error' ? '#7f2d2d' : p.$tone === 'warn' ? '#7a5a1a' : p.$tone === 'ok' ? '#1f5d47' : INK.lineStrong};
  background: ${p => p.$tone === 'error' ? '#2a1015' : p.$tone === 'warn' ? '#261d0c' : p.$tone === 'ok' ? '#0c2219' : INK.raised};
  color: ${p => p.$tone === 'error' ? '#fecaca' : p.$tone === 'warn' ? '#fde68a' : p.$tone === 'ok' ? '#bbf7d0' : INK.soft};
  strong { color: inherit; }
  ul { margin: 6px 0 0 18px; padding: 0; }
`;
